import { CustomError } from '../../common/errors';
import Result from '../../common/result';
import { Credentials } from '../../common/types';
import UserTokenSettable from '../common/userTokenSettable';

export enum ValidationStatus {
  // The validation is in progress
  collecting = 'COLLECTING',
  // The validation is busy processing
  processing = 'PROCESSING',
  // The validation is in review
  inReview = 'IN_REVIEW',
  // The validation is completed successfully
  completed = 'COMPLETED',
  // The validation failed
  failed = 'FAILED',
}

export enum ValidationStepStatus {
  // The UCA is waiting to be collected from the user
  awaitingUserInput = 'AWAITING_USER_INPUT',
  // The UCA cannot be collected until its dependencies are resolved
  awaitingDependency = 'AWAITING_DEPENDENCY',
  // The UCA from the user was deemed to be valid
  accepted = 'ACCEPTED',
  // The UCA from the user was deemed to be invalid
  invalid = 'INVALID',
}

export enum ValidationStep {
  terms = 'terms',
  identityToken = 'identityToken',
  selfie = 'selfie',
  appId = 'appId',
  document = 'document',
  email = 'email',
  emailToken = 'emailToken',
  uniqueness = 'uniqueness',
  faceAuth = 'faceAuth',
}

export type Validation = {
  id: string;
  status: ValidationStatus;
  steps: Step[];
  errorCodes?: string[];
};

export type NoStepValue = Record<string, never>;

export type TermsStepValue = {
  acceptedDeclaration?: string;
  acceptedTermsAndConditionsLink?: string;
  acceptedBiometricsPolicyLink?: string;
};

export type IdentityTokenStepValue = {
  identityAuthToken: string;
};

export type SelfieStepValue = {
  upload: UploadStatus;
};

export type DocumentStepValue = {
  upload: UploadStatus;
};

export type AppIdStepValue = {
  type: string;
  value: string;
};

export type EmailStepValue = {
  email: string;
};

export type EmailCodeStepValue = {
  token: string;
};

export type UniquenessStepValue = {
  identityAuthToken?: string;
};

export type AppIdStepParameters = {
  token: string;
};

export type SelfieStepParameters = {
  selfieUploadUrl: string;
};

export type EmailCodeStepParameters = {
  retriesRemaining: number;
};

export type UploadStatus = 'pending' | 'done';

export type StepValue =
  | TermsStepValue
  | IdentityTokenStepValue
  | SelfieStepValue
  | AppIdStepValue
  | DocumentStepValue
  | EmailStepValue
  | EmailCodeStepValue
  | UniquenessStepValue
  | NoStepValue;

export type Step = {
  name: ValidationStep;
  status: ValidationStepStatus;
  value: StepValue;
  parameters: AppIdStepParameters | SelfieStepParameters | EmailCodeStepParameters | NoStepValue;
  errorCodes?: string[];
};

export type CreateValidationRequest = {
  networkAddress: string;
};

export type ValidationRequest = {
  validationId: string;
};

export type ProcessStepRequest = ValidationRequest & {
  step: ValidationStep;
  value: StepValue;
};

export type GetStepRequest = ValidationRequest & {
  step: ValidationStep;
};

export type FileUploadRequest = ValidationRequest & {
  uploadUrl: string;
  contentType: string;
  base64FileData: string;
};

export type UserStatusRequest = {
  did: string;
  networkAddress: string;
};

export type UserStatus = UserStatusRequest & {
  status: ValidationStatus;
};

export type ValidationResult = Result<CustomError, Validation>;
export type StepResult = Result<CustomError, Step>;
export type UploadResult = Result<CustomError>;
export type UserFeedbackResult = Result<CustomError>;
export type DeleteValidationResult = Result<CustomError>;
export type UserStatusResult = Result<CustomError, UserStatus>;
export type CredentialsResult = Result<CustomError, Credentials>;

export interface ValidationService extends UserTokenSettable {
  create(request: CreateValidationRequest): Promise<ValidationResult>;
  getNextStep(request: ValidationRequest): Promise<StepResult>;
  getStep(request: GetStepRequest): Promise<StepResult>;
  processStep(request: ProcessStepRequest): Promise<StepResult>;
  uploadFile(request: FileUploadRequest): Promise<UploadResult>;
  getValidationResult(request: ValidationRequest): Promise<ValidationResult>;
  deleteValidation(request: ValidationRequest): Promise<DeleteValidationResult>;
  getStatus(request: UserStatusRequest): Promise<UserStatusResult>;
  getCredentials(request: ValidationRequest): Promise<CredentialsResult>;
}
